<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiSheet from '@/components/pos-v2/ui/PosUiSheet.vue';
  import PosUiSwitch from '@/components/pos-v2/ui/PosUiSwitch.vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import { hasDiscount } from '@/domain/catalog/catalog-filters';

  /**
   * «Descuentos» (plan §5.16, DESIGN §3.11): a slim card at the top of the
   * list while the menu has discounted products, with the storefront's
   * «Mostrar» switch. On phones the explanation opens in a sheet.
   */
  const { data, access, online, actions, url, layout } = useCatalogPage();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const tc = (key: string, count: number, params?: Record<string, unknown>) =>
    String(proxy.$tc(key, count, params));

  const phone = computed(() => layout.tier.value === 'phone');
  const sheetOpen = ref(false);
  const switching = ref(false);

  /** The «Con descuento» chip's count: the same products, no second pass. */
  const count = computed(() => data.counts.value.discounted);
  const shown = computed(
    () => !!data.restaurant.value?.settings?.showDiscounts
  );
  /** Only counted while the storefront shows the discounts (the note's case). */
  const withoutPhoto = computed(() =>
    shown.value
      ? data.rows.value.filter(
          (row) => !row.item.picture && hasDiscount(row.item)
        ).length
      : 0
  );

  const summary = computed(() =>
    tc('menuPage.discounts.summary', count.value, { n: count.value })
  );
  const explanation = computed(() =>
    t(shown.value ? 'menuPage.discounts.onHint' : 'menuPage.discounts.offHint')
  );
  const photoNote = computed(() =>
    shown.value && withoutPhoto.value
      ? tc('menuPage.discounts.noPhoto', withoutPhoto.value, {
          m: withoutPhoto.value,
        })
      : null
  );
  const switchTitle = computed(() => {
    if (!access.value.canDiscountsSwitch)
      return t('menuPage.discounts.needsSettings');
    return online.value ? undefined : t('menuPage.errors.offline');
  });

  async function toggle(show: boolean) {
    if (switching.value) return;
    switching.value = true;
    try {
      await actions.setShowDiscounts(show);
    } finally {
      switching.value = false;
    }
  }

  function seeWhich() {
    sheetOpen.value = false;
    url.setFilter('discounted');
  }
</script>

<template>
  <div v-if="count" class="v2-catalog-discounts-card">
    <i class="mdi mdi-tag-outline card-icon" aria-hidden="true" />
    <button
      v-if="phone"
      type="button"
      class="card-text is-button"
      @click="sheetOpen = true"
    >
      <span class="card-title">{{ t('menuPage.discounts.title') }}</span>
      · {{ summary }}
    </button>
    <div v-else class="card-text">
      <span class="card-line">
        <span class="card-title">{{ t('menuPage.discounts.title') }}</span>
        · {{ summary }} · {{ explanation }}
      </span>
      <span v-if="photoNote" class="card-note">
        {{ photoNote }}
        <button type="button" class="note-link" @click="seeWhich">
          {{ t('menuPage.discounts.seeWhich') }}
        </button>
      </span>
    </div>
    <span class="card-switch" :title="switchTitle">
      <PosUiSwitch
        :value="shown"
        :label="t('menuPage.discounts.show')"
        :disabled="!access.canDiscountsSwitch || !online || switching"
        @input="toggle"
      />
    </span>

    <PosUiSheet
      v-if="phone"
      :value="sheetOpen"
      :title="t('menuPage.discounts.title')"
      height="sm"
      @input="sheetOpen = $event"
    >
      <div class="discounts-sheet">
        <h2 class="sheet-title">
          {{ t('menuPage.discounts.title') }} · {{ summary }}
        </h2>
        <p class="sheet-text">{{ explanation }}</p>
        <p v-if="photoNote" class="sheet-text">{{ photoNote }}</p>
        <PosUiButton
          color="neutral"
          variant="outline"
          block
          @click="seeWhich"
          >{{ t('menuPage.discounts.seeWhich') }}</PosUiButton
        >
      </div>
    </PosUiSheet>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-discounts-card {
    @apply tw-mb-3 tw-flex tw-items-center tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-surface tw-py-2 tw-pl-4 tw-pr-2;

    .card-icon {
      @apply tw-mr-3 tw-flex-none tw-text-xl tw-text-neutral-500;
    }

    .card-text {
      @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-text-sm tw-leading-5 tw-text-neutral-600;

      &.is-button {
        @apply tw-min-h-[44px] tw-cursor-pointer tw-flex-row tw-items-center tw-border-0 tw-bg-transparent tw-p-0 tw-text-left;
      }

      .card-title {
        @apply tw-mr-1 tw-font-semibold tw-text-neutral-900;
      }

      .card-note {
        @apply tw-mt-0.5 tw-text-xs tw-text-neutral-500;
      }

      .note-link {
        @apply tw-relative tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-xs tw-font-semibold tw-text-neutral-900 tw-underline;

          // A 44 px tall hit area around the inline link.
          &::after {
            content: '';
            position: absolute;
            top: -12px;
            bottom: -12px;
            left: -4px;
            right: -4px;
          }
      }
    }

    .card-switch {
      @apply tw-ml-3 tw-flex-none;
    }

    .discounts-sheet {
      @apply tw-px-4 tw-pb-4;

      .sheet-title {
        @apply tw-m-0 tw-text-base tw-font-semibold tw-text-neutral-900;
      }

      .sheet-text {
        @apply tw-mb-3 tw-mt-2 tw-text-sm tw-leading-relaxed tw-text-neutral-600;
      }
    }
  }
</style>
